import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import ListItemText from '@mui/material/ListItemText';
import { NavLink as RouterLink } from 'react-router-dom';
import { ICONS } from '@/assets/icons';
import Iconify from '@/components/Iconify';
import { ListItemIconStyle, ListItemStyle, ListItemTextStyle } from '@/components/nav-section/style';
import { NavItemProps } from '@/components/nav-section/type';
import { isExternalLink } from '@/components/nav-section/utils';

/**
 * Minimal@4 component.
 */
export function NavItemRoot({
  item,
  isCollapse,
  open = false,
  active,
  hasActiveSub = false,
  onOpen,
  onNavItemClick,
}: NavItemProps) {
  const { title, path, icon, info, children } = item;

  const renderContent = (
    <>
      {icon ? <ListItemIconStyle>{icon}</ListItemIconStyle> : null}
      {!isCollapse ? (
        <>
          <ListItemTextStyle disableTypography primary={title} isCollapse={isCollapse} />
          {info ? info : null}
          {children ? <ArrowIcon open={open} /> : null}
        </>
      ) : null}
    </>
  );

  if (children) {
    return (
      <ListItemStyle onClick={onOpen} activeRoot={active} hasActiveSub={hasActiveSub}>
        {renderContent}
      </ListItemStyle>
    );
  }

  return isExternalLink(path) ? (
    <ListItemStyle onClick={onNavItemClick} component={Link} href={path} target="_blank" rel="noopener">
      {renderContent}
    </ListItemStyle>
  ) : (
    <ListItemStyle onClick={onNavItemClick} component={RouterLink} to={path} activeRoot={active}>
      {renderContent}
    </ListItemStyle>
  );
}

type NavItemSubProps = Omit<NavItemProps<true>, 'isCollapse'> & {
  onNavItemClick?: VoidFunction;
};

export function NavItemSub({ item, open = false, active = false, onOpen, onNavItemClick }: NavItemSubProps) {
  const { title, path, info, children } = item;

  const renderContent = (
    <>
      <DotIcon active={active} />
      <ListItemText disableTypography primary={title} />
      {info ? info : null}
      {children ? <ArrowIcon open={open} /> : null}
    </>
  );

  if (children) {
    return (
      <ListItemStyle
        onClick={() => {
          onNavItemClick?.();
          onOpen?.();
        }}
        activeSub={active}
        subItem
      >
        {renderContent}
      </ListItemStyle>
    );
  }

  return isExternalLink(path) ? (
    <ListItemStyle onClick={onNavItemClick} component={Link} href={path} target="_blank" rel="noopener" subItem>
      {renderContent}
    </ListItemStyle>
  ) : (
    <ListItemStyle onClick={onNavItemClick} component={RouterLink} to={path} activeSub={active} subItem>
      {renderContent}
    </ListItemStyle>
  );
}

type DotIconProps = {
  active: boolean;
};

function DotIcon({ active }: DotIconProps) {
  return (
    <ListItemIconStyle>
      <Box
        component="span"
        sx={{
          width: 4,
          height: 4,
          borderRadius: '50%',
          bgcolor: 'text.disabled',
          transition: (theme) =>
            theme.transitions.create('transform', {
              duration: theme.transitions.duration.shorter,
            }),
          ...(active && {
            transform: 'scale(2)',
            bgcolor: 'primary.main',
          }),
        }}
      />
    </ListItemIconStyle>
  );
}

type ArrowIconProps = {
  open: boolean;
};

function ArrowIcon({ open }: ArrowIconProps) {
  return (
    <Iconify
      icon={open ? ICONS.EVA_ARROW_IOS_DOWNWARD_FILL : ICONS.EVA_ARROW_IOS_FORWARD_FILL}
      sx={{ width: 16, height: 16, ml: 1 }}
    />
  );
}
